import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  InputNumber,
  notification,
  Row,
  Col,
  Select,
  Typography,
  Divider,
  DatePicker,
  Card,
  Space,
  Table,
  Tag,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PrinterOutlined,
  UserOutlined,
  CalendarOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { API_ENDPOINTS } from '../../../utils/constants';
import logo from "../logo.png";
import moment from "moment";
import qrCodeUrl from "./gpay.jpeg";

const { TextArea } = Input;
const { Option } = Select;
const { Title } = Typography;

const InvoiceForm = () => {
  const [form] = Form.useForm();
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [count, setCount] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [advance, setAdvance] = useState(0);
  const [invoiceDate, setInvoiceDate] = useState(moment());
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [filteredProductOptions, setFilteredProductOptions] = useState([]);
  const [filteredCustomerOptions, setFilteredCustomerOptions] = useState([]);
  const [customerDetails, setCustomerDetails] = useState(null);

  const logoUrl = "/logo.png";
  const paymentQrCodeUrl = "/gpay.jpeg";

  // Helper function to increment invoice number
  const incrementInvoiceNumber = (lastNumber) => {
    const prefix = lastNumber.match(/^[A-Za-z]+/)?.[0] || "INV";
    const numStr = lastNumber.match(/\d+$/)?.[0] || "0";
    const nextNum = parseInt(numStr, 10) + 1;
    const paddedNum = nextNum.toString().padStart(numStr.length, "0");
    return `${prefix}${paddedNum}`;
  };

  const fetchLastInvoiceNumber = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.GET_LAST_INVOICE_NUMBER);
      const result = await response.json();
      if (result.success) {
        // Set the last used number without incrementing
        const lastNumber = result.lastNumber || "INV0000";
        setInvoiceNumber(lastNumber);
        localStorage.setItem("invoiceNumber", lastNumber);
      } else {
        // Fallback to local storage if API fails
        const savedInvoiceNumber = localStorage.getItem("invoiceNumber");
        if (savedInvoiceNumber) {
          setInvoiceNumber(savedInvoiceNumber);
        } else {
          const defaultInvoiceNumber = "INV0001";
          setInvoiceNumber(defaultInvoiceNumber);
        }
      }
    } catch (error) {
      console.error("Failed to fetch last invoice number:", error);
      // Fallback to local storage
      const savedInvoiceNumber = localStorage.getItem("invoiceNumber");
      if (savedInvoiceNumber) {
        setInvoiceNumber(savedInvoiceNumber);
      } else {
        const defaultInvoiceNumber = "INV0001";
        setInvoiceNumber(defaultInvoiceNumber);
      }
    }
  };

  const handleSearch = (value) => {
    if (value) {
      const filteredProducts = productOptions.filter((product) =>
        product.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredProductOptions(filteredProducts);
    } else {
      setFilteredProductOptions(productOptions);
    }
  };

  const handleSearchCustomer = (value) => {
    if (value) {
      const filteredCustomers = customers.filter((customer) =>
        customer.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCustomerOptions(filteredCustomers);
    } else {
      setFilteredCustomerOptions(customers);
    }
  };

  const fetchRemainingAmount = async (customerId) => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.GET_REMAINING_AMOUNT}?customer_name=${customerId}`
      );
      const result = await response.json();
      setCustomerDetails(result);
    } catch (error) {
      console.error("Failed to fetch remaining amount:", error);
      setCustomerDetails(null);
    }
  };

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.GET_CUSTOMER);
        const result = await response.json();
        if (result.success) {
          setCustomers(result.data);
          setFilteredCustomerOptions(result.data);
        } else {
          notification.error({
            message: "Error",
            description: result.message,
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to fetch customers",
        });
      }
    };

    const fetchProducts = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.GET_PRODUCT);
        const result = await response.json();
        if (result.success) {
          setProductOptions(result.data);
          setFilteredProductOptions(result.data);
        } else {
          notification.error({
            message: "Error",
            description: result.message,
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to fetch products",
        });
      }
    };

    fetchCustomers();
    fetchProducts();
    fetchLastInvoiceNumber();
  }, []);

  useEffect(() => {
    // Calculate subtotal and total amount
    const computedSubtotal = products.reduce((total, product) => {
      return total + (product.quantity || 0) * (product.price || 0);
    }, 0);
    
    setSubtotal(computedSubtotal);
    setTotalAmount(computedSubtotal);
  }, [products]);

  const addProduct = () => {
    setProducts([
      ...products,
      { key: count, product: "", quantity: 1, price: 0 },
    ]);
    setCount(count + 1);
  };

  const handleProductChange = (key, field, value) => {
    setProducts(
      products.map((p) => (p.key === key ? { ...p, [field]: value } : p))
    );
  };

  const handleRemoveProduct = (key) => {
    setProducts((prevProducts) =>
      prevProducts.filter((product) => product.key !== key)
    );
  };

  const handleCustomerChange = async (customerName) => {
    try {
      const response = await fetch(
        "https://yogigraphics.in/backend/get_customer.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: customerName }),
        }
      );
      const result = await response.json();
      if (result.success) {
        const customer = result.data;
        form.setFieldsValue({
          address: customer.address,
          number: customer.number,
        });
        fetchRemainingAmount(customerName);
      } else {
        notification.error({
          message: "Error",
          description: result.message,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch customer details",
      });
    }
  };

  const handleProductSelect = (key, productName) => {
    const selectedProduct = productOptions.find((p) => p.name === productName);
    if (selectedProduct) {
      setProducts(
        products.map((p) =>
          p.key === key
            ? { ...p, product: productName, price: selectedProduct.price }
            : p
        )
      );
    }
  };

  const handleSubmitAndPrint = async (values) => {
    try {
      const stockResponse = await fetch(API_ENDPOINTS.GET_PRODUCT_STOCK);
      const stockResult = await stockResponse.json();
      if (!stockResult.success) {
        throw new Error(stockResult.message);
      }

      const stockMap = new Map(
        stockResult.data.map((product) => [product.name, product.quantity])
      );

      for (const product of products) {
        if (!product.product || isNaN(product.price) || isNaN(product.quantity)) {
          notification.error({
            message: "Invalid Product Entry",
            description: `One or more products have missing name, price, or quantity.`,
          });
          return;
        }
      }

      const invoiceResponse = await fetch(API_ENDPOINTS.ADD_INVOICE, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invoiceNumber,
          customer: values.customer,
          address: values.address,
          number: values.number,
          subtotal,
          totalAmount,
          products,
          narration: values.narration,
          advance,
          invoiceDate: invoiceDate ? invoiceDate.format("YYYY-MM-DD") : null,
        }),
      });

      const text = await invoiceResponse.text();
      console.log("🔍 Server response:", text);

      try {
        const invoiceResult = JSON.parse(text);

        if (!invoiceResult.success) {
          throw new Error(invoiceResult.message);
        }

        notification.success({
          message: "Success",
          description: "Invoice added successfully!",
        });

        // Only increment after successful submission
        const nextInvoiceNumber = incrementInvoiceNumber(invoiceNumber);
        setInvoiceNumber(nextInvoiceNumber);
        localStorage.setItem("invoiceNumber", nextInvoiceNumber);

        printInvoice();
        form.resetFields();
        setProducts([]);
        setInvoiceDate(moment());
        setAdvance(0);
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Server returned: " + text,
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "An unexpected error occurred: " + error.message,
      });
    }
  };

  const printInvoice = () => {
    const values = form.getFieldsValue();
    const remainingAmount = totalAmount - advance;

    const invoiceHtml = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <title>Invoice</title>
      <style>
        @page {
          size: A5 portrait;
          margin: 15mm;
        }
        body {
          font-family: 'Arial', sans-serif;
          color: #333;
        }
        .container {
          width: 100%;
          max-width: 520px;
          margin: auto;
          padding: 10px;
          border: 1px solid #ccc;
          background: #fff;
          position: relative;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          border-bottom: 2px solid #000;
          padding-bottom: 10px;
        }
        .header img {
          height: 60px;
        }
        .header h1 {
          font-size: 20px;
          margin: 0 auto;
          text-align: center;
          flex: 1;
        }
        .flex-box {
          display: flex;
          justify-content: space-between;
          gap: 10px;
          margin-top: 10px;
        }
        .box {
          width: 49%;
          border: 1px solid #ccc;
          padding: 6px;
          font-size: 12px;
          border-radius: 4px;
        }
        .box p {
          margin: 4px 0;
        }
        .items {
          width: 100%;
          border-collapse: collapse;
          margin-top: 10px;
          font-size: 11px;
        }
        .items th, .items td {
          border: 1px solid #ccc;
          padding: 5px;
          text-align: left;
        }
        .items th {
          background-color: #f0f0f0;
        }
        .total {
          font-weight: bold;
          text-align: right;
        }
        .amount-summary {
          margin-top: 10px;
          font-size: 12px;
        }
        .amount-row {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
        }
        .footer {
          font-size: 10px;
          border-top: 1px solid #aaa;
          margin-top: 10px;
          padding-top: 5px;
        }
        .signatory {
          font-size: 11px;
          text-align: right;
          margin-top: 40px;
        }
        .qr-code {
          position: absolute;
          bottom: 20px;
          left: 20px;
          height: 80px;
        }
      </style>
    </head>
    <body>
      <div class="container">
        <!-- Header -->
        <div class="header">
          <img src="${logoUrl}" alt="Logo" />
          <h1>INVOICE</h1>
        </div>
  
        <!-- Side-by-side Seller and Buyer Info -->
        <div class="flex-box">
          <!-- Seller -->
          <div class="box">
            <p><strong>Seller:</strong> Yogi Graphics</p>
            <p><strong>Address:</strong> 8, Ramnath Mahadev Shopping Center</p>
            <p> Opp. Zakli Talav, Nr. Bus Stand, Borsad 388540</p>
            <p><strong>Phone:</strong> +91 98255 14255</p>
            <p><strong>Email:</strong> yogiborsad@gmail.com</p>
          </div>
  
          <!-- Buyer -->
          <div class="box">
            <p><strong>Invoice No:</strong> ${invoiceNumber}</p>
            <p><strong>Date:</strong> ${invoiceDate ? invoiceDate.format("DD-MMM-YYYY") : ""}</p>
            <p><strong>Customer:</strong> ${values.customer || ""}</p>
            <p><strong>Address:</strong> ${values.address || ""}</p>
            <p><strong>Phone:</strong> ${values.number || ""}</p>
            <p><strong>Previous Due:</strong> ₹${customerDetails?.total_paid_amount || 0}</p>
          </div>
        </div>
  
        <!-- Items Table -->
        <table class="items">
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Qty</th>
              <th>Rate</th>
              <th>Amt</th>
            </tr>
          </thead>
          <tbody>
            ${
              products.map((item, index) => {
                const qty = parseFloat(item.quantity || 0);
                const price = parseFloat(item.price || 0);
                const amount = (qty * price).toFixed(2);
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${item.product}</td>
                    <td>${qty}</td>
                    <td>₹${price.toFixed(2)}</td>
                    <td>₹${amount}</td>
                  </tr>`;
              }).join("") || ""
            }
          </tbody>
        </table>
  
        <!-- Amount Summary -->
        <div class="amount-summary">
          <div class="amount-row">
            <span><strong>Total Amount:</strong></span>
            <span><strong>₹${totalAmount.toFixed(2)}</strong></span>
          </div>
          <div class="amount-row">
            <span>Advance Paid:</span>
            <span>- ₹${advance.toFixed(2)}</span>
          </div>
          <div class="amount-row">
            <span><strong>Remaining Amount:</strong></span>
            <span><strong>₹${(totalAmount - advance).toFixed(2)}</strong></span>
          </div>
        </div>
  
        <!-- Terms and Signature -->
        <div class="footer">
          <p><strong>Terms & Conditions:</strong> </p>
          <p>(1) All sales are final. Goods once sold cannot be returned, exchanged, or refunded.</p>
          <p>(2) Subject to Borsad jurisdiction.</p>
        </div>
  <br>
        <img src="${paymentQrCodeUrl}" alt="QR Code" class="qr-code" />
  
        <div class="signatory">
          <p>for Yogi Graphics</p>
          <p>Authorized Signature</p>
        </div>
      </div>
    </body>
    </html>
    `;

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(invoiceHtml);
    printWindow.document.close();
    
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  const productColumns = [
    {
      title: '#',
      dataIndex: 'key',
      width: 50,
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Product',
      dataIndex: 'product',
      render: (text, record) => (
        <Select
          value={record.product}
          onChange={(value) => handleProductSelect(record.key, value)}
          onSearch={handleSearch}
          showSearch
          filterOption={false}
          placeholder="Select product"
          style={{ width: '100%', minWidth: 200 }}
        >
          {filteredProductOptions.map((option) => (
            <Option key={option.name} value={option.name}>
              {option.name}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      width: 120,
      render: (text, record) => (
        <InputNumber
          value={record.quantity}
          onChange={(value) => handleProductChange(record.key, 'quantity', value)}
          min={1}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Price (₹)',
      dataIndex: 'price',
      width: 120,
      render: (text, record) => (
        <InputNumber
          value={record.price}
          onChange={(value) => handleProductChange(record.key, 'price', value)}
          min={0}
          step={0.01}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'Total (₹)',
      dataIndex: 'total',
      width: 120,
      render: (_, record) => (
        <Tag color="blue">₹{((record.quantity || 0) * (record.price || 0)).toFixed(2)}</Tag>
      ),
    },
    {
      title: 'Action',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveProduct(record.key)}
        />
      ),
    },
  ];

  return (
    <div className="fade-in">
      <Form form={form} onFinish={handleSubmitAndPrint} layout="vertical">
        <Card 
          title={
            <Space size="middle">
              <UserOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Customer & Invoice Information</span>
            </Space>
          } 
          style={{ marginBottom: 24, borderRadius: '16px' }}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Invoice Number">
                <Input
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  readOnly
                  style={{ fontWeight: 800, color: '#4F46E5', fontSize: '15px' }}
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item
                name="customer"
                label="Customer Name"
                rules={[{ required: true, message: 'Please select a customer' }]}
              >
                <Select
                  showSearch
                  placeholder="Select or search customer"
                  onChange={handleCustomerChange}
                  onSearch={handleSearchCustomer}
                  filterOption={false}
                  size="large"
                >
                  {filteredCustomerOptions.map((customer) => (
                    <Option key={customer.name} value={customer.name}>
                      {customer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item name="address" label="Billing Address">
                <Input placeholder="Customer address" size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item name="number" label="Contact Number">
                <Input placeholder="Phone number" size="large" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label="Invoice Date">
                <DatePicker
                  value={invoiceDate}
                  onChange={(date) => setInvoiceDate(date)}
                  format="YYYY-MM-DD"
                  style={{ width: "100%" }}
                  size="large"
                  suffixIcon={<CalendarOutlined style={{ color: '#4F46E5' }} />}
                />
              </Form.Item>
            </Col>
            {customerDetails && (
              <Col xs={24} sm={12} lg={6}>
                <Form.Item label="Customer Balance Status">
                  <div style={{ 
                    padding: '8px 16px', 
                    background: customerDetails.total_paid_amount > 0 ? '#FFFBEB' : '#ECFDF5', 
                    borderRadius: '10px', 
                    border: customerDetails.total_paid_amount > 0 ? '1px solid #FCD34D' : '1px solid #A7F3D0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    height: '42px'
                  }}>
                    <Typography.Text style={{ color: '#64748B', fontWeight: 600, fontSize: '13px' }}>Previous Outstanding:</Typography.Text>
                    <Typography.Text strong style={{ color: customerDetails.total_paid_amount > 0 ? '#D97706' : '#059669', fontSize: '14px' }}>
                      ₹{customerDetails.total_paid_amount || 0}
                    </Typography.Text>
                  </div>
                </Form.Item>
              </Col>
            )}
          </Row>
        </Card>

        <Card 
          title={
            <Space size="middle">
              <ShoppingCartOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Items & Services</span>
              <Tag color="indigo" style={{ borderRadius: '12px', fontWeight: 600 }}>{products.length} Items</Tag>
            </Space>
          }
          extra={
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={addProduct}
              size="large"
              style={{ borderRadius: '10px' }}
            >
              Add Item Row
            </Button>
          }
          style={{ marginBottom: 24, borderRadius: '16px' }}
        >
          <Table
            columns={productColumns}
            dataSource={products}
            pagination={false}
            size="middle"
            locale={{ emptyText: 'No items added. Click "Add Item Row" to list products.' }}
          />
        </Card>

        <Card 
          title={
            <Space size="middle">
              <DollarOutlined style={{ color: '#4F46E5', fontSize: '18px' }} />
              <span style={{ fontSize: '16px', fontWeight: 700, color: '#0F172A' }}>Payment & Calculation Summary</span>
            </Space>
          }
          style={{ marginBottom: 24, borderRadius: '16px' }}
        >
          <Row gutter={[24, 24]}>
            <Col xs={24} lg={13}>
              <Form.Item name="narration" label="Invoice Narration / Terms">
                <Input.TextArea 
                  rows={4} 
                  placeholder="Additional payment terms, job description, or delivery comments..."
                  style={{ borderRadius: '10px' }}
                />
              </Form.Item>
              <Form.Item label="Advance Payment (₹)">
                <InputNumber
                  value={advance}
                  onChange={(value) => setAdvance(value)}
                  min={0}
                  step={0.01}
                  style={{ width: "100%" }}
                  size="large"
                  placeholder="0.00"
                  prefix="₹"
                />
              </Form.Item>
            </Col>
            
            <Col xs={24} lg={11}>
              <div style={{ 
                background: '#F8FAFC', 
                padding: '24px', 
                borderRadius: '14px',
                border: '1px solid #E2E8F0'
              }}>
                <div style={{ marginBottom: 14 }}>
                  <Row justify="space-between" align="middle">
                    <Col><Typography.Text style={{ color: '#64748B', fontWeight: 600 }}>Gross Subtotal:</Typography.Text></Col>
                    <Col><Typography.Text strong style={{ fontSize: '16px' }}>₹{subtotal.toFixed(2)}</Typography.Text></Col>
                  </Row>
                </div>
                
                <div style={{ marginBottom: 14, paddingTop: 12, borderTop: '1px solid #E2E8F0' }}>
                  <Row justify="space-between" align="middle">
                    <Col><Typography.Text strong style={{ color: '#0F172A', fontSize: '15px' }}>Total Amount:</Typography.Text></Col>
                    <Col><Typography.Text strong style={{ color: '#4F46E5', fontSize: '18px' }}>₹{totalAmount.toFixed(2)}</Typography.Text></Col>
                  </Row>
                </div>
                
                <div style={{ marginBottom: 14 }}>
                  <Row justify="space-between" align="middle">
                    <Col><Typography.Text style={{ color: '#64748B', fontWeight: 600 }}>Advance Received:</Typography.Text></Col>
                    <Col><Typography.Text style={{ color: '#10B981', fontWeight: 700, fontSize: '15px' }}>- ₹{advance.toFixed(2)}</Typography.Text></Col>
                  </Row>
                </div>
                
                <div style={{ paddingTop: 14, borderTop: '2px solid #4F46E5' }}>
                  <Row justify="space-between" align="middle">
                    <Col><Typography.Text strong style={{ fontSize: '17px', color: '#0F172A' }}>Net Amount Due:</Typography.Text></Col>
                    <Col><Typography.Text strong style={{ fontSize: '20px', color: (totalAmount - advance) > 0 ? '#EF4444' : '#10B981' }}>₹{(totalAmount - advance).toFixed(2)}</Typography.Text></Col>
                  </Row>
                </div>
              </div>
            </Col>
          </Row>
          
          <Divider style={{ margin: '24px 0' }} />
          
          <div style={{ textAlign: 'center' }}>
            <Button 
              type="primary" 
              htmlType="submit" 
              size="large"
              icon={<PrinterOutlined />}
              style={{ 
                minWidth: 260,
                height: 52,
                fontSize: 16,
                borderRadius: 12,
                fontWeight: 700
              }}
            >
              Save & Print Sales Invoice
            </Button>
          </div>
        </Card>
      </Form>
    </div>
  );
};

export default InvoiceForm;